import { STUDIO_NAME, STUDIO_TAGLINE } from "../content/site-content.js";
import { assetUrl } from "../api/client.js";
import { trackClickThrough } from "../lib/use-site-tracker.js";

/** The dark gradient tint layered over the hero so the title stays legible. */
const HERO_TINT =
  "linear-gradient(180deg, rgba(11, 11, 13, 0.35), rgba(11, 11, 13, 0.85))";

interface HeroProps {
  /**
   * Owner-managed hero image rendition key from `GET /hero`. When present it is
   * resolved to a full URL (via {@link assetUrl}) and layered, under the dark
   * tint, as the backdrop. When `null`/absent the CSS-only backdrop from
   * `theme.css` is used so the homepage always looks right.
   */
  heroRenditionKey?: string | null;
  /**
   * Photo credit carried over when the hero was chosen from a library image
   * that had one. When present, a small "Photo by: …" line is shown.
   */
  heroPhotoCredit?: string | null;
}

/**
 * Full-viewport hero with a backdrop image and a scroll prompt (Req 1.1).
 *
 * The hero image is supplied by the App shell (fetched from `GET /hero`). When
 * one is configured it is layered as the backdrop; otherwise no inline
 * background is applied and the CSS-only backdrop is used.
 *
 * The SCROLL prompt is the hero call to action, so activating it reports a
 * `hero:scroll` click-through; the call cannot throw (Req 2.4, 14.5).
 */
export function Hero({
  heroRenditionKey,
  heroPhotoCredit,
}: HeroProps): JSX.Element {
  // Only override the CSS backdrop when a hero image is actually configured.
  const backdropStyle = heroRenditionKey
    ? {
        backgroundImage: `${HERO_TINT}, url(${encodeURI(
          assetUrl(heroRenditionKey),
        )})`,
      }
    : undefined;

  return (
    <header id="hero" className="hero" role="banner" style={backdropStyle}>
      <div className="hero__overlay" />
      <div className="hero__content">
        <p className="hero__eyebrow">{STUDIO_TAGLINE}</p>
        <h1 className="hero__title">{STUDIO_NAME}</h1>
      </div>
      <a
        className="hero__scroll"
        href="#gallery"
        aria-label="Scroll to gallery"
        onClick={() => trackClickThrough("hero:scroll")}
      >
        <span className="hero__scroll-text">SCROLL</span>
        <span className="hero__scroll-arrow" aria-hidden="true">
          &#8595;
        </span>
      </a>
      {heroPhotoCredit ? (
        <p className="hero__credit">Photo by: {heroPhotoCredit}</p>
      ) : null}
    </header>
  );
}
